/**
 * Kitchen task link resolution and response parsing.
 *
 * These are the request *edges* of ProductionTaskPage — reading the token out
 * of the WhatsApp link and turning a Response into data or an Error. The
 * production endpoint calls themselves stay in the page, where the state they
 * feed lives.
 */
import { appPathname } from "@/lib/opsBase";

export function readTokenFromQuery() {
  const q = new URLSearchParams(window.location.search);
  return (q.get("token") || "").trim();
}

export function readLinkErrorFromQuery() {
  const q = new URLSearchParams(window.location.search);
  const err = (q.get("error") || "").trim();
  if (err === "invalid") {
    return "This kitchen link is invalid or expired. Ask manager for a fresh link.";
  }
  if (err === "fetch") {
    return "Could not verify this link. Check connection and try the WhatsApp link again.";
  }
  return "";
}

export function parseProductionError(detail, fallback = "Request failed") {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.message || String(d)).filter(Boolean).join("; ") || fallback;
  }
  return fallback;
}

/**
 * Resolve JWT from ?token= or from /p/{role}/{shortCode} via API (WhatsApp short links).
 *
 * `pathname` must already be Ops-stripped. It defaults to `appPathname()` so
 * callers that do not care keep working, but ProductionTaskPage passes it in:
 * guard-ops-base greps the page's own source for the `appPathname()` call, so
 * the strip has to stay visible there and not only here.
 */
export async function resolveProductionToken(expectedRole, pathname = appPathname()) {
  const fromQuery = readTokenFromQuery();
  if (fromQuery) return fromQuery;

  const pathMatch = String(pathname || "").match(
    /^\/p\/(baker|froster|decorator)\/([A-Za-z0-9]+)\/?$/i
  );
  if (!pathMatch) return "";

  const pathRole = pathMatch[1].toLowerCase();
  const code = pathMatch[2];
  if (pathRole !== (expectedRole || "").toLowerCase()) return "";

  const res = await fetch(`/api/p/${encodeURIComponent(pathRole)}/${encodeURIComponent(code)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.redirect_url) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : "Invalid or expired link. Ask manager to resend WhatsApp."
    );
  }
  const u = new URL(data.redirect_url, window.location.origin);
  return (u.searchParams.get("token") || "").trim();
}

export async function parseJson(res) {
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseProductionError(j?.detail));
  return j;
}
